import PublicPortalState from "@publicPortal/utils/InitialState";
import React, { memo, useCallback, useEffect } from "react";
import { atom, useRecoilState, useSetRecoilState } from "recoil";
import BuildingRegulationsSearchState from "./BuildingRegulationsSearchState";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { useNavigate } from "react-router-dom";
import SearchTypeSwitchingTabs from "@publicPortal/components/advancedSearch/SearchTypeSwitchingTabs";
import { SearchType } from "@shared/publicportal/contracts/Search";
import BuildingRegulationsSimpleSearch from "./BuildingRegulationsSimpleSearch";
import BuildingRegulationsAdvancedSearch from "@publicPortal/components/advancedSearch/BuildingRegulationsAdvancedSearch";
import { BuildingRegulationsFilter } from "@shared/publicportal/contracts/BuildingRegulationsFilter";
import { EntityTableStateStorage } from "@mirage/components/entityTable/EntityTableStateStorage";
import SearchActions from "../SearchActions";
import { Serializer } from "@shared/mirage/helpers/Serializer";
import BuildingRegulationsService from "@publicPortal/services/BuildingRegulationsService";
import { PublicPortalMapSearch } from "@publicPortal/components/mapSearch/PublicPortalMapSearch";

const { initialSearchState, initialFilterState } = PublicPortalState.buildingRegulationsSearchState();
const buildingRegulationsSearchState = atom({
    key: "buildingRegulationsSearchState",
    default: initialSearchState,
});
const pp = new PublicPortalRoutingService();
const bcService = new BuildingRegulationsService();

const BuildingRegulationsSearch = memo(() => {
    const [state, setState] = useRecoilState<BuildingRegulationsSearchState>(buildingRegulationsSearchState);
    const setStateModifier = useCallback(
        (modifier: Partial<BuildingRegulationsFilter>) => {
            setState((prev) => {
                return { ...prev, filter: { ...prev.filter, ...modifier } };
            });
        },
        [setState]
    );
    const onSearchTypeChanged = useCallback(
        (newSearchType: SearchType) => {
            setState((prev) => {
                return { ...prev, searchType: newSearchType };
            });
        },
        [setState]
    );

    useEffect(() => {
        const getFilteringLists = async () => {
            const filter = await bcService.getFilteringLists();
            setState((prev) => {
                return {
                    ...prev,
                    appTypes: filter?.ApplicationTypes || [],
                    partnershipTypes: filter?.PartnershipTypes || [],
                    statusTypes: filter?.Statuses || [],
                    wards: filter?.Wards || [],
                    parishes: filter?.Parishes || [],
                };
            });
        };
        getFilteringLists();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <SearchTypeSwitchingTabs searchType={state.searchType} onSearchTypeChanged={onSearchTypeChanged} />
            <FormBySearchType state={state} setStateModifier={setStateModifier} />
            <BcSearchActions state={state} />
        </>
    );
});

const FormBySearchType: React.FC<{ state: BuildingRegulationsSearchState; setStateModifier: (modifier: Partial<BuildingRegulationsFilter>) => void }> = memo(
    ({ state, setStateModifier }) => {
        if (state.searchType === "Simple") {
            return <BuildingRegulationsSimpleSearch state={state} setter={setStateModifier} />;
        } else if (state.searchType === "Advanced") {
            return <BuildingRegulationsAdvancedSearch state={state} setter={setStateModifier} />;
        } else if (state.searchType === "Map") {
            return <PublicPortalMapSearch geometry={state.filter.geometry} setStateModifier={setStateModifier} />;
        }
        return null;
    }
);

const BcSearchActions: React.FC<{ state: BuildingRegulationsSearchState }> = memo(({ state }) => {
    const navigate = useNavigate();
    const setState = useSetRecoilState(buildingRegulationsSearchState);
    const prepareFilter = useCallback(() => {
        switch (state.searchType) {
            case "Simple": {
                return {
                    searchType: state.searchType,
                    appRef: state.filter.appRef,
                    status: state.filter.status,
                };
            }
            case "Advanced": {
                return { ...state.filter, searchType: state.searchType };
            }
            case "Map": {
                return { searchType: state.searchType, geometry: state.filter.geometry };
            }
            default:
                return null;
        }
    }, [state.filter, state.searchType]);
    const onClear = useCallback(() => {
        setState((prev) => {
            return {
                ...prev,
                filter: {
                    ...initialFilterState,
                },
            };
        });
    }, [setState]);
    const onSubmit = useCallback(() => {
        const filter = prepareFilter();
        const searchParams = Serializer.toQueryParamsNotEmptyOnly(filter);
        EntityTableStateStorage.clear("bcRegulations");
        navigate(`${pp.SearchRoute("BuildingRegulations", searchParams)}`);
    }, [navigate, prepareFilter]);

    return <SearchActions onCancelHandler={onClear} onSubmitHandler={onSubmit} />;
});

export default BuildingRegulationsSearch;
